import React, { FC, useEffect } from 'react';
import Container from 'components/container';
import { Heading, Body } from 'components/text';
import Suggestion from './Suggestion';
import useLocalised from 'customHooks/useLocalised/useLocalised';
import { inspiration } from 'data/translationKeys';
import { SearchSuggestion } from './types';

import styles from './SuggestionsDropdown.module.scss';
import { useSearchBarContext } from './context';
import { SEARCH_INPUT_NAME } from './constants';
import { InspirationType } from 'types/types';

import Inspirations from './Inspirations';

interface IInspirationItem {
  inspirationCategories: InspirationType[];
}

const SuggestionsDropdown: FC<IInspirationItem> = ({
  inspirationCategories,
}) => {
  const { state, setState } = useSearchBarContext();

  const {
    debouncedSearchInputValue,
    showSearchSuggestions,
    searchSuggestionList,
    noResultsFound,
    showInspirationCategories,
  } = state;

  const inspiration_text = useLocalised(inspiration);

  // Close suggestions when clicking outside of the search input
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLInputElement;
      if (target.name === SEARCH_INPUT_NAME) {
        return;
      }

      setState({
        showSearchSuggestions: false,
        showInspirationCategories: false,
      });
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldOpen =
    (showSearchSuggestions && debouncedSearchInputValue.length >= 3) ||
    showInspirationCategories;

  if (!shouldOpen) {
    return null;
  }

  type SuggestionCategoryProps = {
    title: typeof suggestionCategories[number]['title'];
    suggestions: SearchSuggestion[];
  };
  const SuggestionCategory: FC<SuggestionCategoryProps> = ({
    title,
    suggestions,
  }) => {
    return (
      <div>
        <Heading as="h5">{title}</Heading>

        <Container mt="20px">
          {suggestions.map(suggestion => (
            <Suggestion key={suggestion.id} suggestion={suggestion} />
          ))}
        </Container>
      </div>
    );
  };

  const suggestionCategories = [
    {
      title: 'Destinations',
      filter: (suggestion: SearchSuggestion) => suggestion.type === 'city',
    },
    {
      title: 'Hotels',
      filter: (suggestion: SearchSuggestion) => suggestion.type === 'hotel',
    },
    {
      title: 'Inspiration',
      filter: (suggestion: SearchSuggestion) => suggestion.type === 'category',
    },
  ];

  return (
    <div>
      {searchSuggestionList?.length === 0 ? (
        noResultsFound ? (
          <div className={styles.noResultFoundContainer}>
            <Body className={styles.noResultFound}>
              Sorry, we could&apos;t find any results for{' '}
              <Body
                color="black"
                weight="demiBold"
                className={styles.noResultFoundText}
              >
                ‘{debouncedSearchInputValue}’
              </Body>
            </Body>
          </div>
        ) : (
          <Inspirations inspirationCategories={inspirationCategories} />
        )
      ) : (
        <div className={styles.container}>
          {suggestionCategories.map(({ title, filter }) => {
            const suggestions = searchSuggestionList?.filter(filter);

            if (!suggestions || suggestions.length === 0) {
              return null;
            }

            return (
              <SuggestionCategory
                key={title}
                title={title}
                suggestions={suggestions}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SuggestionsDropdown;
